import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import EditIcon from '@material-ui/icons/Edit';
import {
  Grid,
  FormControl,
  FormLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { getDistrictEnrollmentSettings } from '../+store/AdvancedSettings/AdvancedSettings.api';
import { saveDistrictEnrollmentSettings } from '../+store/AdvancedSettings/AdvancedSettings.db';

const SOURCES = { teaPublished: 'teaPublished', roster: 'roster', manual: 'manual' };

const SOURCE_HELP = {
  teaPublished: "Using TEA's published grade 3-12 membership (recommended).",
  roster: 'Using live roster counts from the uploaded snapshot.',
  manual: 'Using hand-entered counts below.',
};

// Session-scoped, in-memory cache of the (heavy) enrollment settings payload, keyed
// by org. This component is local-state and remounts every time the user leaves and
// returns to the Settings screen, and its callable recomputes the full roster tally
// each call - so without this, navigation re-triggers a slow load every time. A hard
// refresh clears it (fresh data on reload), and the backend enrollmentCountsCache
// keeps even that first load fast. persist() updates this so saves stay consistent.
const enrollmentSettingsCache = {};
const cacheKeyFor = (profile) => (profile && profile.organizationId) || 'default';

const styles = {
  section: { marginTop: 24 },
  formControl: { minWidth: 240, marginBottom: 8 },
  table: { maxWidth: 640, marginTop: 8 },
  numberCell: { width: 200, textAlign: 'right' },
  note: { color: '#585858', fontSize: 13, marginTop: 4, maxWidth: 640 },
  actionsRow: { marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 },
  savedMsg: { color: '#2e7d32', display: 'inline-flex', alignItems: 'center', marginLeft: 12 },
  editBtn: { marginLeft: 12 },
};

// District-wide setting: choose whether the A-F district report weights campuses by
// live roster grade 3-12 counts (TEA's prescribed method) or by hand-entered counts.
class EnrollmentCounts extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    profile: PropTypes.object,
  };

  state = {
    loading: true,
    error: null,
    source: SOURCES.teaPublished,
    rows: [], // [{ campusNumber, name, rosterCount, publishedCount, manualCount }]
    editedCounts: {}, // campusNumber -> string (manual mode working values)
    saving: false,
    saved: false,
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    const cached = enrollmentSettingsCache[cacheKeyFor(this.props.profile)];
    if (cached) {
      this.setState({
        loading: false,
        source: cached.source,
        rows: cached.rows,
        editedCounts: this.seedFromRows(cached.rows, cached.source),
      });
      return;
    }
    try {
      const { data } = await getDistrictEnrollmentSettings({});
      const raw = data && data.enrollmentSource;
      const source = SOURCES[raw] || SOURCES.teaPublished;
      const rows = (data && data.campuses) || [];
      enrollmentSettingsCache[cacheKeyFor(this.props.profile)] = { source, rows };
      this.setState({
        loading: false,
        source,
        rows,
        editedCounts: this.seedFromRows(rows, source),
      });
    } catch (err) {
      this.setState({ loading: false, error: 'Could not load enrollment settings.' });
    }
  };

  // Effective grade 3-12 count shown for a non-manual source: TEA published where
  // available (falling back to roster, mirroring the report's gap-fill), or roster.
  baseCount = (row, source) => {
    if (source === SOURCES.roster) return row.rosterCount || 0;
    return row.publishedCount != null ? row.publishedCount : row.rosterCount || 0;
  };

  // Seed editable values from saved manual counts where present, otherwise the count
  // shown for the current source — so switching to Manual "starts with what was there."
  seedFromRows = (rows, source) =>
    rows.reduce((acc, r) => {
      acc[r.campusNumber] = String(r.manualCount != null ? r.manualCount : this.baseCount(r, source));
      return acc;
    }, {});

  handleSourceChange = (event) => {
    const source = event.target.value;
    if (source === SOURCES.manual) {
      // Entering manual: seed from the counts currently shown (TEA published or roster).
      this.setState((prev) => ({ source, saved: false, editedCounts: this.seedFromRows(prev.rows, prev.source) }));
    } else {
      this.setState({ source, saved: false }, this.persist);
    }
  };

  // The "edit" affordance from a read-only source: flips to manual, seeded from it.
  handleStartEditing = () => {
    this.setState((prev) => ({
      source: SOURCES.manual,
      saved: false,
      editedCounts: this.seedFromRows(prev.rows, prev.source),
    }));
  };

  handleCountChange = (campusNumber) => (event) => {
    const value = event.target.value;
    if (value !== '' && !/^\d+$/.test(value)) return; // whole numbers only
    this.setState((prev) => ({ editedCounts: { ...prev.editedCounts, [campusNumber]: value }, saved: false }));
  };

  persist = async () => {
    const { profile } = this.props;
    const { source, rows, editedCounts } = this.state;
    this.setState({ saving: true, saved: false, error: null });
    try {
      let manualEnrollmentCounts = null;
      if (source === SOURCES.manual) {
        manualEnrollmentCounts = rows.reduce((acc, r) => {
          acc[r.campusNumber] = Number(editedCounts[r.campusNumber] || 0);
          return acc;
        }, {});
      }
      await saveDistrictEnrollmentSettings(profile, { enrollmentSource: source, manualEnrollmentCounts });
      // Reflect saved manual values back into rows so the view stays consistent.
      const updatedRows =
        source === SOURCES.manual
          ? rows.map((r) => ({ ...r, manualCount: manualEnrollmentCounts[r.campusNumber] }))
          : rows;
      // Keep the session cache in step with what we just saved so returning to this
      // screen reflects the change without a refetch.
      enrollmentSettingsCache[cacheKeyFor(profile)] = { source, rows: updatedRows };
      this.setState({ saving: false, saved: true, rows: updatedRows });
    } catch (err) {
      this.setState({ saving: false, error: 'Could not save. You may not have permission.' });
    }
  };

  render() {
    const { classes } = this.props;
    const { loading, error, source, rows, editedCounts, saving, saved } = this.state;
    const isManual = source === SOURCES.manual;

    if (loading) {
      return (
        <div className={classes.section}>
          <FormLabel component="legend">Enrollment Counts</FormLabel>
          <CircularProgress size={20} style={{ marginTop: 12 }} />
        </div>
      );
    }

    return (
      <div className={classes.section}>
        <FormLabel component="legend">Enrollment Counts</FormLabel>
        <Typography className={classes.note}>
          Controls the grade 3-12 student counts used to proportionally weight each campus in the district A-F report.
          TEA Published (default) uses TEA's official grade 3-12 membership list — the same figures TEA uses for the
          district rating. Roster uses live counts from the uploaded snapshot. Manual lets you override with
          hand-entered counts.
        </Typography>

        <Grid container direction="column" className={classes.section}>
          <FormControl className={classes.formControl}>
            <InputLabel shrink>Enrollment Count Source</InputLabel>
            <Select value={source} onChange={this.handleSourceChange} name="enrollmentSource" displayEmpty>
              <MenuItem value={SOURCES.teaPublished}>TEA Published (grades 3-12)</MenuItem>
              <MenuItem value={SOURCES.roster}>Roster (from file)</MenuItem>
              <MenuItem value={SOURCES.manual}>Manual Settings</MenuItem>
            </Select>
            <FormHelperText>{SOURCE_HELP[source] || ''}</FormHelperText>
          </FormControl>

          {!isManual && (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<EditIcon />}
              onClick={this.handleStartEditing}
              className={classes.editBtn}
              style={{ alignSelf: 'flex-start' }}
            >
              Edit / override counts
            </Button>
          )}
        </Grid>

        {error && <Typography color="error">{error}</Typography>}

        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Campus</TableCell>
              <TableCell>Name</TableCell>
              <TableCell className={classes.numberCell}>Grade 3-12 Enrollment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.campusNumber}>
                <TableCell>{row.campusNumber}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell className={classes.numberCell}>
                  {isManual ? (
                    <TextField
                      value={editedCounts[row.campusNumber] != null ? editedCounts[row.campusNumber] : ''}
                      onChange={this.handleCountChange(row.campusNumber)}
                      inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                      placeholder={String(this.baseCount(row, source))}
                    />
                  ) : (
                    this.baseCount(row, source)
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isManual && (
          <div className={classes.actionsRow}>
            <Button variant="contained" color="primary" onClick={this.persist} disabled={saving}>
              {saving ? 'Saving ...' : 'Save Manual Counts'}
            </Button>
            {saved && (
              <span className={classes.savedMsg}>
                <CheckCircleIcon fontSize="small" style={{ marginRight: 4 }} />
                Saved
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(EnrollmentCounts);
